import { Key, ReactNode } from 'react';

import * as uuid from 'uuid';
import { confirmDialog, Toast } from 'app/components/external';
import { Subject } from 'rxjs';

const reference = <T>(key: string): T | undefined  => {
  if (key.startsWith('#')) return ((window as any)[key] as T);
}

export interface IAction {
  icon?: string;
  label?: string;
  className?: string;
}

export interface IConfirm {
  message: string;
  title?: string;
  icon?: string;

  accept?: IAction;
  reject?: IAction;

  tag?: string;
  dismissable?: boolean;
}

export const confirm = async ({ message, title, icon, accept, reject, tag, dismissable }: IConfirm): Promise<boolean> => new Promise(resolve => {
  confirmDialog({ message, icon,
    header: title,

    tagKey: (tag ?? 'default'),
    closable: dismissable,
    draggable: false,

    acceptLabel: (accept?.label ?? 'Yes'),
    acceptClassName: accept?.className,
    rejectLabel: (reject?.label ?? 'No'),
    rejectClassName: reject?.className,

    accept() { resolve(true); },
    reject() { resolve(false); },
  });
});

const templates = new Map<Key, IDialog>();
const change = new Subject<void>();

export const subscribe = (callback: () => void) => {
  const subscription = change.subscribe(callback);

  return () => subscription.unsubscribe();
};

export interface IDialog {
  key?: Key;
  header?: ReactNode;
  template: ReactNode;
  footer?: ReactNode;
  visible?: boolean;
  closable?: boolean;
}

export const show = ({ key, header, template, footer, visible, closable }: Partial<IDialog>): void => {
  if (!template) return;

  templates.set(key ??= uuid.v4(), { key, header, template, footer,
    visible: (visible ?? true),
    closable: (closable ?? true),
  });
  change.next();
};
export const hide = (key: IDialog['key']): void => {
  if (!key || !templates.has(key)) return;

  templates.get(key)!.visible = false;
  change.next();
};

export const map = (mapper: (key: Key, props: IDialog) => ReactNode): ReactNode => {
  return Array.from(templates).map(([key, props]) => mapper(key, props));
};

export interface IToast {
  title?: ReactNode;
  message?: ReactNode;

  severity?: 'success' | 'info' | 'warn' | 'error';
  icon?: string;
  closable?: boolean;
}

export const toast = ({ title, message, severity, icon, closable }: IToast): void => {
  const instance = reference<Toast>('#toast');
  if (!instance) return;

  instance.show({ severity, icon, closable,
    summary: title,
    detail: message,
  });
};
